import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button, Input } from "@/components/ui";
import { ShieldCheck, Activity, Lock } from "lucide-react";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";

const DEMO = [
  { label: "Care Manager", email: "carmen.reyes@gomco.ai", password: "Care123!" },
  { label: "Provider", email: "dr.patel@gomco.ai", password: "Provider123!" },
  { label: "Member", email: "member.jordan@gomco.ai", password: "Member123!" },
];

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaCode, setMfaCode] = useState("");

  const goHome = (u) => {
    toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
    navigate(u.role === "member" ? "/member-portal" : u.role === "provider" ? "/provider-portal" : "/dashboard");
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    try {
      const res = await login(email, password);
      if (res?.mfaRequired) { setMfaStep(true); toast.message("Enter your authenticator code"); return; }
      goHome(res);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    try {
      const u = await verifyMfa(mfaCode);
      goHome(u);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const quickLogin = async (creds) => {
    setBusy(true);
    try {
      const res = await login(creds.email, creds.password);
      if (res?.mfaRequired) { setMfaStep(true); toast.message("Enter your authenticator code"); return; }
      goHome(res);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  void googleLogin;

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <ThemeToggle className="fixed top-4 right-4 z-50" />
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-brand-950 text-white p-12 relative overflow-hidden">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand-800/40 blur-3xl" />
        <div className="absolute right-10 bottom-10 h-72 w-72 rounded-full bg-brand-700/20 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <img src="/brand/gomco-icon-dark.png" alt="Go MCO" className="h-10 w-10 object-contain" />
          <div>
            <p className="font-display font-extrabold text-2xl tracking-tight">Go MCO</p>
            <p className="text-[11px] uppercase tracking-[0.3em] text-brand-300">Managed care, in motion</p>
          </div>
        </div>
        <div className="relative space-y-6 max-w-md">
          <h1 className="font-display text-4xl font-bold leading-tight tracking-tight">
            Whole-person care management, engineered for MCOs.
          </h1>
          <p className="text-brand-100 leading-relaxed">
            Risk stratification, assessments, care plans, utilization &amp; quality management, end-to-end billing,
            and configurable AI — on one secure, interoperable platform.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-4">
            {[["HIPAA-aligned", ShieldCheck], ["SOC 2 (in progress)", Lock], ["NIST 800-53", Activity]].map(([t, Icon]) => (
              <div key={t} className="rounded-lg border border-white/10 bg-white/5 p-3">
                <Icon className="h-5 w-5 text-brand-300 mb-2" strokeWidth={1.75} />
                <p className="text-xs font-semibold">{t}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-brand-400">Built for Trillium Health Resources · Extensible to any Managed Care Organization</p>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-card">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/brand/gomco-icon-dark.png" alt="Go MCO" className="h-10 w-10 object-contain" />
            <p className="font-display font-extrabold text-2xl">Go MCO</p>
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight">{mfaStep ? "Two-factor authentication" : "Sign in"}</h2>
          <p className="text-sm text-muted-foreground mt-1">{mfaStep ? "Enter the 6-digit code from your authenticator app." : "Access your clinical workspace"}</p>

          {mfaStep ? (
            <form onSubmit={submitMfa} className="mt-8 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Authentication code</label>
                <Input className="mt-1.5 text-center text-lg tracking-[0.4em]" inputMode="numeric" maxLength={6} value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" data-testid="mfa-code" autoFocus required />
              </div>
              <Button type="submit" size="lg" className="w-full" disabled={busy || mfaCode.length !== 6} data-testid="mfa-verify">
                {busy ? "Verifying…" : "Verify & sign in"}
              </Button>
              <button type="button" onClick={() => { setMfaStep(false); setMfaCode(""); }} className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="mfa-back">
                ← Back to sign in
              </button>
            </form>
          ) : (
            <>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Email</label>
                  <Input className="mt-1.5" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@gomco.ai" data-testid="login-email" required />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Password</label>
                  <Input className="mt-1.5" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" data-testid="login-password" required />
                </div>
                <Button type="submit" size="lg" className="w-full" disabled={busy} data-testid="login-submit">
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>

              <div className="mt-8 rounded-lg border border-border bg-surface p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-2">Demo accounts</p>
                <div className="grid grid-cols-3 gap-2">
                  {DEMO.map((d) => (
                    <button key={d.email} onClick={() => quickLogin(d)} disabled={busy}
                      className="text-center rounded-md border border-border bg-card px-2 py-2 text-xs hover:border-brand-500 hover:bg-brand-50 transition-colors disabled:opacity-50"
                      data-testid={`demo-${d.label.toLowerCase().replace(/ /g, "-")}`}>
                      <span className="font-semibold text-foreground block">{d.label}</span>
                      <span className="text-muted-foreground text-[10px]">Quick login</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
